import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import GoogleTranslate from '../GoogleTranslate/GoogleTranslate';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const navigate = useNavigate();

  // Fetch suggestions when user types
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const res = await fetch(
          `https://accdfser.vercel.app/api/search/suggestions?q=${encodeURIComponent(searchQuery)}&limit=5`
        );
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setShowSuggestions(data.suggestions && data.suggestions.length > 0);
      } catch (err) {
        console.error('❌ Error fetching suggestions:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Update dropdown position when it should be shown
  useEffect(() => {
    if (showSuggestions && searchRef.current) {
      const updatePosition = () => {
        if (searchRef.current) {
          const rect = searchRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showSuggestions, suggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (query = searchQuery) => {
    if (query.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.text);
    setShowSuggestions(false);
    navigate(suggestion.url);
  };

  return (
    <header className="bg-white shadow-md relative z-[100]">
      <div className="container mx-auto px-4 py-1">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Logo Only */}
          <div className="flex items-center gap-4 flex-1">
            <div className="flex-1 flex items-center">
              <div className="logo h-16 md:h-24 flex items-center justify-start">
                <Link to="/" className="h-full block">
                  <img 
                    src="/assets/images/logo.jpeg" 
                    alt="Logo" 
                    className="h-full w-auto object-contain" 
                  />
                </Link>
              </div>
            </div>
          </div>

          {/* Translate + Search */}
          <div className="flex items-center gap-3 mt-0">
            <GoogleTranslate />
            <div className="hidden md:flex items-center gap-0 relative" ref={searchRef}>
            <input
              type="text"
              placeholder="Search here"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                if (suggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              className="px-4 py-2 border border-green-600 rounded-l-md focus:outline-none focus:ring-1 focus:ring-green-600 w-48 text-blue-500 placeholder-blue-400 text-sm"
            />
            <button
              onClick={() => handleSearch()}
              className="bg-nav-green text-white px-3 sm:px-4 py-2 rounded-r-md hover:bg-opacity-90 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>

            {/* Suggestions Dropdown - Using Portal to render above all elements */}
            {showSuggestions && createPortal(
              <div
                ref={suggestionsRef}
                className="fixed bg-white border border-green-600 rounded-md shadow-xl z-[9999] max-h-64 overflow-y-auto"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  zIndex: 9999
                }}
              >
                {isLoading ? (
                  <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
                ) : suggestions.length > 0 ? (
                  <>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-4 py-2 hover:bg-green-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="text-sm text-blue-600 font-medium">
                          {suggestion.text}
                        </div>
                        <div className="text-xs text-gray-500 capitalize">
                          {suggestion.type}
                        </div>
                      </div>
                    ))}
                    <div
                      onClick={() => handleSearch()}
                      className="px-4 py-2 bg-green-50 hover:bg-green-100 cursor-pointer border-t border-green-200 text-center"
                    >
                      <span className="text-sm text-green-700 font-medium">
                        Search for "{searchQuery}"
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">No suggestions found</div>
                )}
              </div>,
              document.body
            )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;


